import { useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { API_BASE_URL } from '@/client/config';
import { getMonthName } from '@/client/lib/date';
import { downloadCSV } from '@/client/lib/csv';
import { Download, FileSpreadsheet } from 'lucide-react';
import type {
  TranscriptionUsageResponse,
  LiveCaptionUsageResponse,
  AllClientsTranscriptionResponse,
  AllClientsCaptionResponse,
} from '@shared/types/transcription';
import type { ClientResponse } from '@shared/types';

import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Label } from '@/client/components/ui/label';
import { Switch } from '@/client/components/ui/switch';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Separator } from '@/client/components/ui/separator';
import { ClientSelector } from '@/client/components/client-selector';
import { DateRangePicker } from '@/client/components/date-range-picker';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'captioning/reports',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: CaptioningReportsComponent,
});

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function minutesToHours(minutes: number): string {
  const hours = minutes / 60;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(hours);
}

function CaptioningReportsComponent() {
  const { getAccessTokenSilently } = useAuth0();

  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickRange, setQuickRange] = useState('');

  // Client selection state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);
  const [allClients, setAllClients] = useState(false);

  // Report state - single client
  const [transcriptionData, setTranscriptionData] = useState<TranscriptionUsageResponse | null>(null);
  const [captionData, setCaptionData] = useState<LiveCaptionUsageResponse | null>(null);

  // Report state - all clients
  const [allClientsTranscription, setAllClientsTranscription] = useState<AllClientsTranscriptionResponse | null>(null);
  const [allClientsCaptions, setAllClientsCaptions] = useState<AllClientsCaptionResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);
  const [hideZeroRows, setHideZeroRows] = useState(false);

  // Can generate report if dates are set AND (allClients OR specific client selected)
  const canGenerateReport = startDate && endDate && (allClients || selectedClientId);

  const handleClientChange = (clientId: string, client: ClientResponse | null) => {
    setSelectedClientId(clientId);
    setSelectedClient(client);
    // Clear all report data when client changes
    setTranscriptionData(null);
    setCaptionData(null);
    setAllClientsTranscription(null);
    setAllClientsCaptions(null);
    setConfirmPending(false);
  };

  const handleAllClientsChange = (checked: boolean) => {
    setAllClients(checked);
    // Clear all report data when toggle changes
    setTranscriptionData(null);
    setCaptionData(null);
    setAllClientsTranscription(null);
    setAllClientsCaptions(null);
    setConfirmPending(false);
  };

  const handleGenerateReport = async () => {
    if (!canGenerateReport) return;

    if (!confirmPending) {
      setConfirmPending(true);
      return;
    }

    setConfirmPending(false);
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (allClients) {
        // All-clients mode: fetch aggregate data for all clients
        const [transcriptionRes, captionRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/transcription/usage/all?startDate=${startDate}&endDate=${endDate}`,
            { headers }
          ),
          fetch(
            `${API_BASE_URL}/api/captions/usage/all?startDate=${startDate}&endDate=${endDate}`,
            { headers }
          ),
        ]);

        if (!transcriptionRes.ok) {
          throw new Error(`Failed to fetch transcription data: ${transcriptionRes.statusText}`);
        }
        if (!captionRes.ok) {
          throw new Error(`Failed to fetch caption data: ${captionRes.statusText}`);
        }

        const [transcription, captions] = await Promise.all([
          transcriptionRes.json() as Promise<AllClientsTranscriptionResponse>,
          captionRes.json() as Promise<AllClientsCaptionResponse>,
        ]);

        setAllClientsTranscription(transcription);
        setAllClientsCaptions(captions);
        // Clear single-client data
        setTranscriptionData(null);
        setCaptionData(null);
      } else {
        // Single-client mode: fetch data for specific client
        const [transcriptionRes, captionRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/transcription/usage?clientID=${selectedClientId}&startDate=${startDate}&endDate=${endDate}`,
            { headers }
          ),
          fetch(
            `${API_BASE_URL}/api/captions/usage?clientID=${selectedClientId}&startDate=${startDate}&endDate=${endDate}`,
            { headers }
          ),
        ]);

        if (!transcriptionRes.ok) {
          throw new Error(`Failed to fetch transcription data: ${transcriptionRes.statusText}`);
        }
        if (!captionRes.ok) {
          throw new Error(`Failed to fetch caption data: ${captionRes.statusText}`);
        }

        const [transcription, captions] = await Promise.all([
          transcriptionRes.json() as Promise<TranscriptionUsageResponse>,
          captionRes.json() as Promise<LiveCaptionUsageResponse>,
        ]);

        setTranscriptionData(transcription);
        setCaptionData(captions);
        // Clear all-clients data
        setAllClientsTranscription(null);
        setAllClientsCaptions(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExportTranscription = () => {
    if (!transcriptionData) return;

    const headers = ['Year', 'Month', 'Event Count', 'Total Minutes', 'Total Hours'];
    const rows: string[][] = transcriptionData.monthly.map(row => [
      row.year.toString(),
      getMonthName(row.month),
      row.eventCount.toString(),
      row.totalMinutes.toString(),
      (row.totalMinutes / 60).toFixed(2),
    ]);

    rows.push([
      'TOTAL',
      '',
      transcriptionData.totals.eventCount.toString(),
      transcriptionData.totals.totalMinutes.toString(),
      (transcriptionData.totals.totalMinutes / 60).toFixed(2),
    ]);

    const clientName = selectedClient?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'client';
    downloadCSV(`transcription_${clientName}_${transcriptionData.dateRange.start}_to_${transcriptionData.dateRange.end}.csv`, headers, rows);
  };

  const handleExportCaptions = () => {
    if (!captionData) return;

    const headers = ['Year', 'Month', 'Session Count', 'Total Minutes', 'Total Hours'];
    const rows: string[][] = captionData.monthly.map(row => [
      row.year.toString(),
      getMonthName(row.month),
      row.sessionCount.toString(),
      row.totalDurationMinutes.toString(),
      (row.totalDurationMinutes / 60).toFixed(2),
    ]);

    rows.push([
      'TOTAL',
      '',
      captionData.totals.sessionCount.toString(),
      captionData.totals.totalDurationMinutes.toString(),
      (captionData.totals.totalDurationMinutes / 60).toFixed(2),
    ]);

    const clientName = selectedClient?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'client';
    downloadCSV(`live_captions_${clientName}_${captionData.dateRange.start}_to_${captionData.dateRange.end}.csv`, headers, rows);
  };

  // Export functions for all-clients mode
  const handleExportAllClientsTranscription = () => {
    if (!allClientsTranscription) return;

    const headers = ['Client ID', 'Client Name', 'Events', 'Total Minutes', 'Total Hours'];
    const rows: string[][] = allClientsTranscription.clients.map(client => [
      client.clientID,
      client.clientName,
      client.eventCount.toString(),
      client.totalMinutes.toString(),
      client.totalHours.toFixed(2),
    ]);

    rows.push([
      'GRAND TOTAL',
      `(${allClientsTranscription.grandTotals.clientCount} clients)`,
      allClientsTranscription.grandTotals.eventCount.toString(),
      allClientsTranscription.grandTotals.totalMinutes.toString(),
      allClientsTranscription.grandTotals.totalHours.toFixed(2),
    ]);

    downloadCSV(`transcription_all_clients_${allClientsTranscription.dateRange.start}_to_${allClientsTranscription.dateRange.end}.csv`, headers, rows);
  };

  const handleExportAllClientsCaptions = () => {
    if (!allClientsCaptions) return;

    const headers = ['Client ID', 'Client Name', 'Sessions', 'Total Minutes', 'Total Hours'];
    const rows: string[][] = allClientsCaptions.clients.map(client => [
      client.clientID,
      client.clientName,
      client.sessionCount.toString(),
      client.totalDurationMinutes.toString(),
      client.totalDurationHours.toFixed(2),
    ]);

    rows.push([
      'GRAND TOTAL',
      `(${allClientsCaptions.grandTotals.clientCount} clients)`,
      allClientsCaptions.grandTotals.sessionCount.toString(),
      allClientsCaptions.grandTotals.totalDurationMinutes.toString(),
      allClientsCaptions.grandTotals.totalDurationHours.toFixed(2),
    ]);

    downloadCSV(`live_captions_all_clients_${allClientsCaptions.dateRange.start}_to_${allClientsCaptions.dateRange.end}.csv`, headers, rows);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-semibold">Captioning Reports</h3>
          <CacheTimestamp cachedAt={
            transcriptionData?.cacheInfo?.cachedAt ??
            captionData?.cacheInfo?.cachedAt ??
            allClientsTranscription?.cacheInfo?.cachedAt ??
            allClientsCaptions?.cacheInfo?.cachedAt ??
            null
          } />
        </div>

        {/* Controls Section */}
        <div className="border rounded-lg p-4 space-y-4 bg-card">
          {/* Date Range Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Date Range</span>
            <div className="flex flex-wrap gap-4 items-end">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                quickRange={quickRange}
                onQuickRangeChange={setQuickRange}
                showToday={false}
                onDateChange={() => setConfirmPending(false)}
              />
            </div>
          </div>

          {/* Client Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Client</span>
            <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="all-clients"
                  checked={allClients}
                  onCheckedChange={(checked) => handleAllClientsChange(checked === true)}
                />
                <Label htmlFor="all-clients" className="cursor-pointer">
                  All Clients
                </Label>
              </div>

              <span className="text-muted-foreground">or</span>

              <ClientSelector
                value={selectedClientId}
                onChange={handleClientChange}
                className="min-w-[250px]"
                label=""
                disabled={allClients}
              />

              <Button
                onClick={handleGenerateReport}
                disabled={!canGenerateReport || loading}
                variant={confirmPending ? 'destructive' : 'default'}
                className="min-w-[175px]"
              >
                {loading ? (
                  'Loading...'
                ) : confirmPending ? (
                  'Click to Confirm'
                ) : (
                  <>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>

              <ResetFiltersButton
                routePath="/captioning/reports"
                defaultValues={{ clientID: undefined, startDate: undefined, endDate: undefined, quickRange: undefined, allClients: false }}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded">
            <p className="m-0 text-red-800 font-medium">Error: {error}</p>
          </div>
        )}

        {/* Results Section - Single Client */}
        {(transcriptionData || captionData) && (
          <div className="space-y-8">
            <div className="text-sm text-muted-foreground">
              Report for Client <Badge variant="outline" className="text-xs font-mono">{selectedClientId}</Badge> from <Badge variant="outline" className="text-xs font-mono">{startDate} to {endDate}</Badge>
            </div>

            {/* Transcription Table */}
            {transcriptionData && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <h4 className="text-lg font-semibold">VOD Transcription</h4>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <div className="flex items-center gap-2">
                      <Switch
                        id="hide-zero-transcription"
                        checked={hideZeroRows}
                        onCheckedChange={setHideZeroRows}
                      />
                      <Label htmlFor="hide-zero-transcription" className="text-sm text-muted-foreground cursor-pointer">
                        Hide empty records
                      </Label>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportTranscription}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>

                {/* Rate Config Info */}
                {transcriptionData.rateConfig && (
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="font-normal">
                      <span className="font-medium mr-1">Rate:</span>
                      {formatCurrency(transcriptionData.rateConfig.effectiveRate)}/min
                    </Badge>
                    {transcriptionData.rateConfig.rateDiscount > 0 && (
                      <Badge variant="outline" className="font-normal">
                        <span className="font-medium mr-1">Discount:</span>
                        {transcriptionData.rateConfig.rateDiscount}% = {formatCurrency(transcriptionData.rateConfig.rateAfterDiscount)}/min
                      </Badge>
                    )}
                    <Badge variant="outline" className="font-normal">
                      <span className="font-medium mr-1">Included:</span>
                      {formatNumber(transcriptionData.rateConfig.monthlyBulkMinutes)} min/month
                    </Badge>
                    {(quickRange === 'this-year' || quickRange === 'last-year') && (
                      <Badge variant="outline" className="font-normal">
                        <span className="font-medium mr-1">Contracted Annual:</span>
                        {formatNumber(transcriptionData.rateConfig.annualContractedHours)} hrs
                      </Badge>
                    )}
                  </div>
                )}

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Year</TableHead>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Events</TableHead>
                        <TableHead className="text-right">Total Minutes</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transcriptionData.monthly.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No transcription data for this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {transcriptionData.monthly
                            .filter(row => !hideZeroRows || row.eventCount > 0 || row.totalMinutes > 0)
                            .map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.year}</TableCell>
                              <TableCell>{getMonthName(row.month)}</TableCell>
                              <TableCell className="text-right">{formatNumber(row.eventCount)}</TableCell>
                              <TableCell className="text-right">{formatNumber(row.totalMinutes)}</TableCell>
                              <TableCell className="text-right">{minutesToHours(row.totalMinutes)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-semibold">
                            <TableCell colSpan={2}>Total</TableCell>
                            <TableCell className="text-right">
                              {formatNumber(transcriptionData.totals.eventCount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(transcriptionData.totals.totalMinutes)}
                            </TableCell>
                            <TableCell className="text-right">
                              {minutesToHours(transcriptionData.totals.totalMinutes)}
                            </TableCell>
                          </TableRow>
                          {transcriptionData.totals.billableMinutes !== undefined && (
                            <TableRow className="bg-muted/30 text-sm">
                              <TableCell colSpan={4} className="text-right">
                                Billable (after {formatNumber(transcriptionData.totals.includedMinutes ?? 0)} included):
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatNumber(transcriptionData.totals.billableMinutes)} min
                                {transcriptionData.totals.estimatedCost !== undefined && (
                                  <> = {formatCurrency(transcriptionData.totals.estimatedCost)}</>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Live Captions Table */}
            {captionData && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <h4 className="text-lg font-semibold">Live AI Captioning</h4>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <div className="flex items-center gap-2">
                      <Switch
                        id="hide-zero-captions"
                        checked={hideZeroRows}
                        onCheckedChange={setHideZeroRows}
                      />
                      <Label htmlFor="hide-zero-captions" className="text-sm text-muted-foreground cursor-pointer">
                        Hide empty records
                      </Label>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportCaptions}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>

                {/* Rate Config Info */}
                {captionData.rateConfig && (
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="font-normal">
                      <span className="font-medium mr-1">Rate:</span>
                      {formatCurrency(captionData.rateConfig.effectiveRate)}/min
                    </Badge>
                    {captionData.rateConfig.rateDiscount > 0 && (
                      <Badge variant="outline" className="font-normal">
                        <span className="font-medium mr-1">Discount:</span>
                        {captionData.rateConfig.rateDiscount}% = {formatCurrency(captionData.rateConfig.rateAfterDiscount)}/min
                      </Badge>
                    )}
                    <Badge variant="outline" className="font-normal">
                      <span className="font-medium mr-1">Included:</span>
                      {formatNumber(captionData.rateConfig.monthlyBulkMinutes)} min/month
                    </Badge>
                    {(quickRange === 'this-year' || quickRange === 'last-year') && (
                      <Badge variant="outline" className="font-normal">
                        <span className="font-medium mr-1">Contracted Annual:</span>
                        {formatNumber(captionData.rateConfig.annualContractedHours)} hrs
                      </Badge>
                    )}
                  </div>
                )}

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Year</TableHead>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Sessions</TableHead>
                        <TableHead className="text-right">Total Minutes</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {captionData.monthly.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No live captioning data for this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {captionData.monthly
                            .filter(row => !hideZeroRows || row.sessionCount > 0 || row.totalDurationMinutes > 0)
                            .map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.year}</TableCell>
                              <TableCell>{getMonthName(row.month)}</TableCell>
                              <TableCell className="text-right">{formatNumber(row.sessionCount)}</TableCell>
                              <TableCell className="text-right">{formatNumber(row.totalDurationMinutes)}</TableCell>
                              <TableCell className="text-right">{minutesToHours(row.totalDurationMinutes)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-semibold">
                            <TableCell colSpan={2}>Total</TableCell>
                            <TableCell className="text-right">
                              {formatNumber(captionData.totals.sessionCount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(captionData.totals.totalDurationMinutes)}
                            </TableCell>
                            <TableCell className="text-right">
                              {minutesToHours(captionData.totals.totalDurationMinutes)}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Section - All Clients */}
        {(allClientsTranscription || allClientsCaptions) && (
          <div className="space-y-8">
            <div className="text-sm text-muted-foreground">
              All Clients Report from {startDate} to {endDate}
            </div>

            {/* All Clients Transcription Table */}
            {allClientsTranscription && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <h4 className="text-lg font-semibold">VOD Transcription - All Clients</h4>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <div className="flex items-center gap-2">
                      <Switch
                        id="hide-zero-all-transcription"
                        checked={hideZeroRows}
                        onCheckedChange={setHideZeroRows}
                      />
                      <Label htmlFor="hide-zero-all-transcription" className="text-sm text-muted-foreground cursor-pointer">
                        Hide empty records
                      </Label>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportAllClientsTranscription}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client ID</TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead className="text-right">Events</TableHead>
                        <TableHead className="text-right">Total Minutes</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allClientsTranscription.clients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No transcription data for this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {allClientsTranscription.clients
                            .filter(client => !hideZeroRows || client.eventCount > 0 || client.totalMinutes > 0)
                            .map((client) => (
                            <TableRow key={client.clientID}>
                              <TableCell>{client.clientID}</TableCell>
                              <TableCell>{client.clientName}</TableCell>
                              <TableCell className="text-right">{formatNumber(client.eventCount)}</TableCell>
                              <TableCell className="text-right">{formatNumber(client.totalMinutes)}</TableCell>
                              <TableCell className="text-right">{client.totalHours.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-semibold">
                            <TableCell>Grand Total</TableCell>
                            <TableCell className="text-muted-foreground">
                              ({allClientsTranscription.grandTotals.clientCount} clients)
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(allClientsTranscription.grandTotals.eventCount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(allClientsTranscription.grandTotals.totalMinutes)}
                            </TableCell>
                            <TableCell className="text-right">
                              {allClientsTranscription.grandTotals.totalHours.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* All Clients Live Captions Table */}
            {allClientsCaptions && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <h4 className="text-lg font-semibold">Live AI Captioning - All Clients</h4>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <div className="flex items-center gap-2">
                      <Switch
                        id="hide-zero-all-captions"
                        checked={hideZeroRows}
                        onCheckedChange={setHideZeroRows}
                      />
                      <Label htmlFor="hide-zero-all-captions" className="text-sm text-muted-foreground cursor-pointer">
                        Hide empty records
                      </Label>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportAllClientsCaptions}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client ID</TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead className="text-right">Sessions</TableHead>
                        <TableHead className="text-right">Total Minutes</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allClientsCaptions.clients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No live captioning data for this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {allClientsCaptions.clients
                            .filter(client => !hideZeroRows || client.sessionCount > 0 || client.totalDurationMinutes > 0)
                            .map((client) => (
                            <TableRow key={client.clientID}>
                              <TableCell>{client.clientID}</TableCell>
                              <TableCell>{client.clientName}</TableCell>
                              <TableCell className="text-right">{formatNumber(client.sessionCount)}</TableCell>
                              <TableCell className="text-right">{formatNumber(client.totalDurationMinutes)}</TableCell>
                              <TableCell className="text-right">{client.totalDurationHours.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-semibold">
                            <TableCell>Grand Total</TableCell>
                            <TableCell className="text-muted-foreground">
                              ({allClientsCaptions.grandTotals.clientCount} clients)
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(allClientsCaptions.grandTotals.sessionCount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(allClientsCaptions.grandTotals.totalDurationMinutes)}
                            </TableCell>
                            <TableCell className="text-right">
                              {allClientsCaptions.grandTotals.totalDurationHours.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
